import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Waves, CalendarClock, Ticket, Receipt, Baby, Anchor, LogOut, X, Shell,
  Sparkles, Gem, Compass, MessageCircle, Plus, Gift, FileText, ShieldCheck, CreditCard, Trash2, CalendarClock as RescheduleIcon, Copy, Wallet,
} from "lucide-react";
import RegistrationForm from "@/components/RegistrationForm";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bubbles } from "@/components/Motifs";

const SKILL_ICONS = [Waves, Sparkles, Shell, Gem, Compass, Anchor, Shell, Gem, Compass];

export default function ParentDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [content, setContent] = useState({});
  const [giftCode, setGiftCode] = useState("");
  const [otterCode, setOtterCode] = useState("");
  const [reschedule, setReschedule] = useState(null);
  const [payBooking, setPayBooking] = useState(null);

  const load = () => api.get("/parent/dashboard").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); api.get("/content").then((r) => setContent(r.data)); }, []);

  const redeemGift = async () => {
    if (!giftCode.trim()) return toast.error("Enter a gift code.");
    try {
      const { data: res } = await api.post("/gifts/redeem", { code: giftCode });
      toast.success(`Redeemed! ${res.lessons} lessons added to your account.`);
      setGiftCode(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const redeemOtter = async () => {
    if (!otterCode.trim()) return toast.error("Enter your Rec Center code.");
    try {
      const { data: res } = await api.post("/otter/redeem", { code: otterCode.trim() });
      toast.success(`${res.lessons} Little Otters sessions added!`);
      setOtterCode(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cancel = async (id) => {
    try {
      const { data: res } = await api.post(`/parent/bookings/${id}/cancel`);
      toast.success(res.within_24 ? "Cancelled (within 24h — may count as used)." : "Lesson cancelled.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="min-h-screen grid place-items-center bg-cream text-navy">Loading your dashboard…</div>;

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-navy text-cream relative overflow-hidden">
        <Bubbles count={10} dark />
        <div className="relative mx-auto max-w-6xl px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3"><img src="/assets/logo.png" alt="Float Now" className="h-12 w-12 object-contain" /><span className="font-serif text-xl">Float Now</span></Link>
          <div className="flex items-center gap-3">
            <Button asChild className="rounded-full bg-coral hover:bg-[#C95B50] text-white" data-testid="dash-book"><Link to="/book"><Anchor size={16} className="mr-2" />Book Another</Link></Button>
            <Button onClick={() => { logout(); navigate("/"); }} variant="ghost" className="text-cream hover:text-coral" data-testid="dash-logout"><LogOut size={18} /></Button>
          </div>
        </div>
        <div className="relative mx-auto max-w-6xl px-6 pb-12 pt-4">
          <h1 className="font-serif text-4xl">Welcome back, {data.user.name.split(" ")[0]}!</h1>
          <p className="mt-2 text-cream/70">Here's your family's water-safety adventure at a glance.</p>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-10 space-y-10">
        {/* stats */}
        <div className="grid gap-4 sm:grid-cols-3">
          <Stat icon={CalendarClock} label="Upcoming Lessons" value={data.upcoming.length} />
          <Stat icon={Ticket} label="Package Lessons Left" value={data.remaining_lessons} />
          <Stat icon={Baby} label="Children" value={data.children.length} />
        </div>

        {/* upcoming */}
        <Section title="Upcoming Lessons" icon={CalendarClock}>
          {data.upcoming.length === 0 ? <Empty text="No upcoming lessons yet." cta /> : (
            <div className="space-y-3">
              {data.upcoming.map((b) => (
                <div key={b.id} data-testid={`upcoming-${b.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white border border-gold/30 p-5">
                  <div>
                    <p className="font-serif text-lg text-navy">{b.program_name}</p>
                    <p className="text-sm text-navy/60">{b.child_name} · {format(new Date(b.date.length === 10 ? b.date + "T00:00:00" : b.date), "EEE, MMM d")} at {formatTime(b.time)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs rounded-full px-3 py-1 ${b.status === "confirmed" ? "bg-teal/15 text-teal" : b.status === "awaiting_venmo" ? "bg-[#3D95CE]/15 text-[#2E7DB0]" : "bg-gold/20 text-navy"}`}>{b.status === "confirmed" ? "Confirmed" : b.status === "awaiting_venmo" ? "Awaiting Venmo" : "Pending payment"}</span>
                    {b.source === "admin" && !b.paid && !b.program_rec_center && b.program_price > 0 && <Button onClick={() => setPayBooking(b)} size="sm" className="rounded-full bg-coral hover:bg-[#C95B50] text-white" data-testid={`pay-${b.id}`}>Pay ${Number(b.program_price).toFixed(2)}</Button>}
                    <Button onClick={() => setReschedule(b)} variant="ghost" size="sm" className="text-teal hover:bg-teal/10" data-testid={`reschedule-${b.id}`}><RescheduleIcon size={16} className="mr-1" />Reschedule</Button>
                    <Button onClick={() => cancel(b.id)} variant="ghost" size="sm" className="text-coral hover:bg-coral/10" data-testid={`cancel-${b.id}`}><X size={16} className="mr-1" />Cancel</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* children progress */}
        <Section title="Swim-Skill Progress" icon={Sparkles}>
          {data.children.length === 0 ? <Empty text="Add a child when you book your first lesson." cta /> : (
            <div className="grid gap-6 md:grid-cols-2">
              {data.children.map((c) => (
                <ChildCard key={c.id} child={c} skillKeys={data.skill_keys} onReload={load} />))}
            </div>
          )}
        </Section>

        {/* payments */}
        <Section title="Payment History" icon={Receipt}>
          {data.payments.length === 0 ? <Empty text="No payments yet." /> : (
            <div className="rounded-2xl bg-white border border-gold/30 overflow-hidden">
              {data.payments.map((p) => (
                <div key={p.id} className="flex justify-between px-5 py-4 border-b border-gold/15 last:border-0">
                  <div><p className="text-navy font-medium">{p.label}</p><p className="text-xs text-navy/50">{p.date ? format(new Date(p.date.length === 10 ? p.date + "T00:00:00" : p.date), "MMM d, yyyy") : ""}</p></div>
                  <span className="font-serif text-lg text-navy">${Number(p.amount).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* messages */}
        <ParentMessages />

        {/* little otters */}
        <Section title="Little Otters (Rec Center)" icon={Waves}>
          <div className="rounded-2xl bg-white border border-[#3D95CE]/30 p-5" data-testid="otter-summary">
            {(data.otter_remaining > 0 || data.otter_attended > 0) && (
              <div className="flex flex-wrap gap-8 mb-4">
                <div><p className="font-serif text-3xl text-[#2E7DB0]">{data.otter_attended || 0}</p><p className="text-sm text-navy/60">sessions attended</p></div>
                <div><p className="font-serif text-3xl text-navy">{data.otter_remaining || 0}</p><p className="text-sm text-navy/60">sessions remaining</p></div>
              </div>
            )}
            <Label>Have a Rec Center code?</Label>
            <div className="mt-1 flex gap-2">
              <Input value={otterCode} onChange={(e) => setOtterCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && redeemOtter()} data-testid="otter-code" placeholder="Rec Center code" className="bg-white" />
              <Button type="button" variant="outline" onClick={redeemOtter} data-testid="otter-redeem" className="rounded-full border-[#3D95CE] text-[#3D95CE] hover:bg-[#3D95CE] hover:text-white shrink-0">Add sessions</Button>
            </div>
            <p className="mt-2 text-xs text-navy/50">Enter the code from the North Platte Rec Center to add your Little Otters sessions, then reserve Mon/Wed/Fri 9:00–12:00 times from the booking page.</p>
          </div>
        </Section>

        {/* saved cards */}
        <SavedCards />

        {/* gift cards owned */}
        {data.gifts && data.gifts.length > 0 && (
          <Section title="Your Gift Cards" icon={Gift}>
            <div className="rounded-2xl bg-white border border-gold/30 overflow-hidden" data-testid="gift-cards">
              {data.gifts.map((g) => (
                <div key={g.code} data-testid={`gift-card-${g.code}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-gold/15 last:border-0">
                  <div>
                    <p className="font-mono font-bold text-navy">{g.code}</p>
                    <p className="text-xs text-navy/60">{g.package_name} · {g.lessons} lessons{g.recipient_name ? ` · for ${g.recipient_name}` : ""} · <span className="text-coral">Not yet redeemed</span></p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(g.code); toast.success("Gift code copied!"); }} data-testid={`copy-gift-${g.code}`} className="rounded-full border-navy/30 text-navy"><Copy size={14} className="mr-1" />Copy code</Button>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* redeem gift */}
        <Section title="Redeem a Gift" icon={Gift}>
          <div className="rounded-2xl bg-white border border-gold/30 p-5 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]"><Label>Gift code</Label><Input value={giftCode} onChange={(e) => setGiftCode(e.target.value.toUpperCase())} placeholder="GIFT-XXXXXXXX" className="mt-1 bg-cream" data-testid="redeem-code" /></div>
            <Button onClick={redeemGift} data-testid="redeem-submit" className="rounded-full bg-coral hover:bg-[#C95B50] text-white"><Gift size={16} className="mr-2" />Redeem</Button>
          </div>
        </Section>

        <div className="flex flex-wrap gap-3">
          <ChangePasswordDialog />
          <Dialog open={contactOpen} onOpenChange={setContactOpen}>
            <DialogTrigger asChild><Button variant="outline" className="rounded-full border-navy text-navy hover:bg-navy hover:text-cream" data-testid="contact-instructor"><MessageCircle size={16} className="mr-2" />Contact Instructor</Button></DialogTrigger>
            <DialogContent className="bg-cream"><DialogHeader><DialogTitle className="font-serif text-navy text-2xl">Contact Your Instructor</DialogTitle></DialogHeader>
              <p className="text-navy/70">Reach out any time — we're happy to help.</p>
              <div className="mt-4 space-y-2 text-navy"><p>📞 {content.phone}</p><p>✉️ {content.email}</p></div>
              <p className="text-xs text-navy/40 mt-4">This website is not monitored for emergencies. In an emergency, call 911.</p>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      {reschedule && <RescheduleDialog booking={reschedule} onClose={() => setReschedule(null)} onDone={() => { setReschedule(null); load(); }} />}
      {payBooking && <PayLessonDialog booking={payBooking} onClose={() => setPayBooking(null)} />}
    </div>
  );
}

function PayLessonDialog({ booking, onClose }) {
  const [code, setCode] = useState("");
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [venmo, setVenmo] = useState(null);
  const apply = async () => {
    if (!code.trim()) { setInfo(null); return; }
    try {
      const { data } = await api.post("/discounts/preview", { code: code.trim(), program_id: booking.program_id });
      setInfo(data);
      if (!data.valid) toast.error(data.message || "Invalid code.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const payCard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/parent/bookings/${booking.id}/pay-checkout`, { discount_code: info?.valid ? code.trim() : null, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  const payVenmo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/parent/bookings/${booking.id}/pay-venmo`, { discount_code: info?.valid ? code.trim() : null });
      setVenmo(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const price = Number(booking.program_price || 0);
  if (venmo) {
    const username = (venmo.venmo_handle || "").replace(/^@/, "").trim();
    const amount = Number(venmo.amount || 0).toFixed(2);
    const note = encodeURIComponent(`${venmo.title || "Float Now"} — Float Now`);
    const payLink = username ? `https://venmo.com/${username}?txn=pay&amount=${amount}&note=${note}` : (venmo.venmo_link || "");
    return (<Dialog open onOpenChange={(o) => { if (!o) onClose(); }}><DialogContent className="bg-cream" data-testid="pay-venmo-dialog">
      <DialogHeader><DialogTitle className="font-serif text-2xl text-navy flex items-center gap-2"><Wallet size={22} className="text-[#3D95CE]" /> Pay with Venmo</DialogTitle></DialogHeader>
      <p className="text-navy/75 text-sm">Your lesson is reserved! To confirm, send <strong className="text-navy">${amount}</strong> on Venmo to:</p>
      <div className="my-2 rounded-2xl bg-[#3D95CE]/10 border border-[#3D95CE]/30 p-4 text-center">
        <p className="font-serif text-2xl text-[#2E7DB0]" data-testid="pay-venmo-handle">{venmo.venmo_handle || "Ask your instructor"}</p>
        {payLink && <a href={payLink} target="_blank" rel="noreferrer" data-testid="pay-venmo-link" className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#3D95CE] hover:bg-[#2E7DB0] text-white px-6 py-3 text-sm font-medium transition-colors"><Wallet size={16} /> Pay ${amount} in Venmo</a>}
        {venmo.venmo_qr_url && <div className="mt-4"><p className="text-xs text-navy/50 mb-1">or scan to pay</p><img src={venmo.venmo_qr_url} alt="Venmo QR code" data-testid="pay-venmo-qr" className="mx-auto h-40 w-40 rounded-xl bg-white p-2 border border-[#3D95CE]/20" /></div>}
      </div>
      <p className="text-navy/70 text-sm">{venmo.venmo_instructions}</p>
      <DialogFooter><Button onClick={onClose} data-testid="pay-venmo-done" className="rounded-full bg-[#3D95CE] hover:bg-[#2E7DB0] text-white">Done</Button></DialogFooter>
    </DialogContent></Dialog>);
  }
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-cream max-w-sm" data-testid="pay-lesson-dialog">
        <DialogHeader><DialogTitle className="font-serif text-navy text-2xl">Pay for your lesson</DialogTitle></DialogHeader>
        <p className="text-sm text-navy/70">{booking.program_name} · {booking.child_name}</p>
        <div className="mt-1 flex items-baseline gap-2">
          {info?.valid ? (<><span className="text-navy/40 line-through">${info.original.toFixed(2)}</span><span className="font-serif text-2xl text-coral" data-testid="pay-amount">${info.discounted.toFixed(2)}</span></>) : <span className="font-serif text-2xl text-navy" data-testid="pay-amount">${price.toFixed(2)}</span>}
        </div>
        {info?.valid && <p className="text-sm text-teal" data-testid="pay-discount-ok">✓ {info.description} — you save ${info.savings.toFixed(2)}.</p>}
        <div className="mt-3"><Label>Discount code (optional)</Label>
          <div className="mt-1 flex gap-2">
            <Input value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setInfo(null); }} onKeyDown={(e) => e.key === "Enter" && apply()} data-testid="pay-discount" placeholder="Enter code" className="bg-white" />
            <Button type="button" variant="outline" onClick={apply} data-testid="pay-apply" className="rounded-full border-navy text-navy hover:bg-navy hover:text-cream shrink-0">Apply</Button>
          </div>
          {info && !info.valid && <p className="mt-1.5 text-sm text-coral" data-testid="pay-discount-error">{info.message}</p>}
        </div>
        <div className="mt-4 flex flex-col gap-2">
          <Button onClick={payCard} disabled={busy} data-testid="pay-confirm" className="rounded-full bg-coral hover:bg-[#C95B50] text-white"><CreditCard size={16} className="mr-2" />{busy ? "Please wait…" : "Pay by card"}</Button>
          <Button onClick={payVenmo} disabled={busy} data-testid="pay-venmo" variant="outline" className="rounded-full border-[#3D95CE] text-[#3D95CE] hover:bg-[#3D95CE] hover:text-white"><Wallet size={16} className="mr-2" />Pay with Venmo</Button>
        </div>
        <p className="mt-2 text-xs text-navy/40">Card payments are processed securely by Stripe. Venmo reserves your spot; it's confirmed once your instructor receives payment.</p>
      </DialogContent>
    </Dialog>
  );
}

function RescheduleDialog({ booking, onClose, onDone }) {
  const [date, setDate] = useState(booking.date);
  const [slots, setSlots] = useState([]);
  const [selTime, setSelTime] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setSelTime("");
    if (!date) return;
    api.get(`/availability/slots?date=${date}&instructor=${booking.instructor}`).then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [date, booking.instructor]);
  const submit = async () => {
    if (!selTime) return toast.error("Please pick a new time.");
    setBusy(true);
    try {
      await api.post(`/parent/bookings/${booking.id}/reschedule`, { date, time: selTime });
      toast.success("Lesson rescheduled!");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-cream" data-testid="reschedule-dialog">
        <DialogHeader><DialogTitle className="font-serif text-navy text-2xl">Reschedule lesson</DialogTitle></DialogHeader>
        <p className="text-sm text-navy/70">{booking.program_name} with {booking.instructor_name}. Currently {format(new Date(booking.date.length === 10 ? booking.date + "T00:00:00" : booking.date), "EEE, MMM d")} at {formatTime(booking.time)}.</p>
        <div className="mt-2"><Label>New date</Label>
          <Input type="date" value={date} min={format(new Date(), "yyyy-MM-dd")} onChange={(e) => setDate(e.target.value)} data-testid="reschedule-date" className="mt-1 bg-white" />
        </div>
        <div className="mt-3">
          <Label>Open times</Label>
          {slots.length === 0 ? (
            <p className="mt-2 text-sm text-navy/50">No open times this day. Try another date.</p>
          ) : (
            <div className="mt-2 flex flex-wrap gap-2">
              {slots.map((t) => (
                <button key={t} onClick={() => setSelTime(t)} data-testid={`reschedule-slot-${t}`}
                  className={`rounded-full px-4 py-2 text-sm border transition-colors ${selTime === t ? "bg-coral text-white border-coral" : "bg-white border-gold/40 text-navy hover:border-coral"}`}>{formatTime(t)}</button>
              ))}
            </div>
          )}
          <p className="mt-2 text-xs text-navy/40">Times shown in America/Chicago.</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-navy/60">Cancel</Button>
          <Button onClick={submit} disabled={!selTime || busy} data-testid="reschedule-confirm" className="rounded-full bg-coral hover:bg-[#C95B50] text-white disabled:opacity-40">{busy ? "Saving…" : "Confirm new time"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChildCard({ child, skillKeys, onReload }) {
  const [regOpen, setRegOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const skills = child.skills || {};
  const total = skillKeys.length;
  const mastered = skillKeys.filter((k) => (skills[k] || 0) >= 2).length;
  const pct = Math.round((skillKeys.reduce((a, k) => a + (skills[k] || 0), 0) / (total * 2)) * 100);
  return (
    <div data-testid={`child-${child.id}`} className="rounded-3xl bg-white border border-gold/30 p-6 shadow-[0_8px_32px_rgba(15,32,39,0.05)]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 grid place-items-center rounded-2xl bg-lavender/20 text-lavender overflow-hidden">
            {child.photo_url ? <img src={child.photo_url} alt={child.name} className="h-full w-full object-cover" /> : <Baby size={22} />}
          </div>
          <div><p className="font-serif text-xl text-navy">{child.name}</p>{child.age && <p className="text-xs text-navy/50">Age {child.age}</p>}</div>
        </div>
        <span className="text-xs rounded-full bg-teal/15 text-teal px-3 py-1">{mastered}/{total} badges</span>
      </div>
      <div className="mt-4"><Progress value={pct} className="h-2 bg-cream" /><p className="mt-1 text-xs text-navy/50">{pct}% toward mastery</p></div>
      <div className="mt-5 grid grid-cols-3 gap-3">
        {skillKeys.map((k, i) => {
          const lvl = skills[k] || 0;
          const Icon = SKILL_ICONS[i % SKILL_ICONS.length];
          return (
            <div key={k} title={k} className="flex flex-col items-center text-center">
              <div className={`h-11 w-11 grid place-items-center rounded-full transition-colors ${lvl >= 2 ? "bg-gold text-navy" : lvl === 1 ? "bg-teal/20 text-teal" : "bg-cream text-navy/25 border border-gold/20"}`}><Icon size={18} /></div>
              <span className="mt-1 text-[9px] leading-tight text-navy/60">{k}</span>
            </div>
          );
        })}
      </div>
      {child.instructor_notes && (<div className="mt-5 rounded-2xl bg-cream p-4 text-sm text-navy/75"><p className="text-xs uppercase tracking-wide text-teal font-semibold mb-1">Instructor Notes</p>{child.instructor_notes}</div>)}
      <div className="mt-4 flex items-center gap-2 flex-wrap border-t border-gold/15 pt-4">
        {child.waiver_signed ? (
          <>
            <span className="inline-flex items-center gap-1.5 text-xs rounded-full bg-teal/15 text-teal px-3 py-1"><ShieldCheck size={13} /> Waiver signed</span>
            <Button size="sm" variant="outline" onClick={() => setViewOpen(true)} data-testid={`view-waiver-${child.id}`} className="rounded-full border-navy/30 text-navy"><FileText size={14} className="mr-1" />View signed waiver</Button>
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-1.5 text-xs rounded-full bg-coral/15 text-coral px-3 py-1">Registration &amp; waiver needed</span>
            <Button size="sm" onClick={() => setRegOpen(true)} data-testid={`complete-waiver-${child.id}`} className="rounded-full bg-coral hover:bg-[#C95B50] text-white"><FileText size={14} className="mr-1" />Complete Registration</Button>
          </>
        )}
      </div>
      <RegistrationForm open={regOpen} onOpenChange={setRegOpen} childName={child.name} mode="edit" initial={child.forms}
        onSubmit={async (forms) => { await api.put(`/children/${child.id}`, { name: child.name, age: child.age, forms }); toast.success("Registration & waiver saved."); setRegOpen(false); onReload && onReload(); }} />
      <RegistrationForm open={viewOpen} onOpenChange={setViewOpen} childName={child.name} mode="view" initial={child.forms} onSubmit={async () => {}} />
    </div>
  );
}

function SavedCards() {
  const [cards, setCards] = useState(null);
  const load = () => api.get("/parent/payment-methods").then((r) => setCards(r.data.cards)).catch(() => setCards([]));
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm("Remove this saved card?")) return;
    try { await api.delete(`/parent/payment-methods/${id}`); toast.success("Card removed."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const brandLabel = (b) => (b || "card").replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <Section title="Saved Cards" icon={CreditCard}>
      {cards === null ? (
        <Empty text="Loading saved cards…" />
      ) : cards.length === 0 ? (
        <Empty text="No saved cards yet. Your card is saved automatically the next time you check out, so booking is faster." />
      ) : (
        <div className="rounded-2xl bg-white border border-gold/30 overflow-hidden" data-testid="saved-cards">
          {cards.map((c) => (
            <div key={c.id} data-testid={`saved-card-${c.id}`} className="flex items-center justify-between px-5 py-4 border-b border-gold/15 last:border-0">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 grid place-items-center rounded-xl bg-teal/10 text-teal"><CreditCard size={20} /></div>
                <div>
                  <p className="text-navy font-medium">{brandLabel(c.brand)} •••• {c.last4}</p>
                  <p className="text-xs text-navy/50">Expires {String(c.exp_month).padStart(2, "0")}/{c.exp_year}</p>
                </div>
              </div>
              <Button onClick={() => remove(c.id)} variant="ghost" size="sm" className="text-coral hover:bg-coral/10" data-testid={`remove-card-${c.id}`}><Trash2 size={16} className="mr-1" />Remove</Button>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-3xl bg-white border border-gold/30 p-6 flex items-center gap-4 shadow-[0_8px_32px_rgba(15,32,39,0.05)]">
      <div className="h-14 w-14 grid place-items-center rounded-2xl bg-teal/10 text-teal"><Icon size={26} /></div>
      <div><p className="font-serif text-3xl text-navy">{value}</p><p className="text-sm text-navy/60">{label}</p></div>
    </div>
  );
}
function ParentMessages() {
  const [instr, setInstr] = useState("krystal");
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [unread, setUnread] = useState(0);
  const [notify, setNotify] = useState(true);
  const [digest, setDigest] = useState(false);
  const loadUnread = () => api.get("/parent/messages/unread").then((r) => setUnread(r.data.unread)).catch(() => {});
  const load = (who) => api.get(`/parent/messages?instructor=${who}`).then((r) => setMsgs(r.data)).catch(() => setMsgs([]));
  useEffect(() => { load(instr); loadUnread(); }, [instr]);
  useEffect(() => { api.get("/settings/notifications").then((r) => { setNotify(r.data.notify_messages); setDigest(r.data.digest_weekly); }).catch(() => {}); }, []);
  const toggleNotify = async (v) => { setNotify(v); try { await api.put("/settings/notifications", { notify_messages: v, digest_weekly: digest }); toast.success(v ? "Email alerts on." : "Email alerts off."); } catch { setNotify(!v); toast.error("Could not update setting."); } };
  const toggleDigest = async (v) => { setDigest(v); try { await api.put("/settings/notifications", { notify_messages: notify, digest_weekly: v }); toast.success(v ? "You'll get a weekly summary instead." : "You'll get an email for each message."); } catch { setDigest(!v); toast.error("Could not update setting."); } };
  const send = async () => {
    if (!body.trim()) return toast.error("Type a message first.");
    try { await api.post("/parent/messages", { instructor: instr, body }); setBody(""); load(instr); loadUnread(); toast.success("Message sent!"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const names = { krystal: "Dr. Krystal", kristi: "Dr. Kristi" };
  return (
    <Section title="Messages" icon={MessageCircle}>
      <div className="rounded-2xl bg-white border border-gold/30 p-5" data-testid="parent-messages">
        {unread > 0 && <p className="mb-3 text-sm text-coral font-medium" data-testid="parent-msg-unread">You have {unread} new repl{unread === 1 ? "y" : "ies"} — open a thread below to read.</p>}
        <label className="flex items-center justify-between gap-2 mb-3 pb-3 border-b border-gold/20 text-sm text-navy/70">Email me when I get a new reply<Switch checked={notify} onCheckedChange={toggleNotify} data-testid="parent-notify-toggle" /></label>
        {notify && (
          <label className="flex items-center gap-2 mb-3 pb-3 border-b border-gold/20 text-sm text-navy/60 cursor-pointer">
            <input type="checkbox" checked={digest} onChange={(e) => toggleDigest(e.target.checked)} data-testid="parent-digest-checkbox" className="h-4 w-4 rounded border-gold/50 accent-teal" />
            Send a weekly summary instead of an email for every message
          </label>
        )}
        <div className="flex gap-2 mb-3">
          {["krystal", "kristi"].map((w) => (
            <button key={w} onClick={() => setInstr(w)} data-testid={`msg-to-${w}`}
              className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${instr === w ? "bg-navy text-cream border-navy" : "bg-white border-gold/40 text-navy hover:border-teal"}`}>{names[w]}</button>
          ))}
        </div>
        <div className="max-h-64 overflow-y-auto space-y-2 mb-3">
          {msgs.length === 0 ? <p className="text-sm text-navy/50 py-4 text-center">No messages yet. Say hello to {names[instr]}!</p> :
            msgs.map((m) => (
              <div key={m.id} className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${m.sender === "parent" ? "ml-auto bg-teal/15 text-navy" : "bg-cream text-navy border border-gold/30"}`}>
                <p className="whitespace-pre-line">{m.body}</p>
                <p className="text-[10px] text-navy/40 mt-1">{format(new Date(m.created_at), "MMM d, h:mm a")}</p>
              </div>
            ))}
        </div>
        <div className="flex gap-2">
          <Input value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={`Message ${names[instr]}…`} data-testid="msg-input" className="bg-white" />
          <Button onClick={send} data-testid="msg-send" className="rounded-full bg-coral hover:bg-[#C95B50] text-white shrink-0">Send</Button>
        </div>
      </div>
    </Section>
  );
}

function Section({ title, icon: Icon, children }) {
  return (<div><h2 className="font-serif text-2xl text-navy flex items-center gap-2 mb-5"><Icon size={22} className="text-teal" /> {title}</h2>{children}</div>);
}
function Empty({ text, cta }) {
  return (<div className="rounded-2xl border border-dashed border-gold/40 bg-white/50 p-8 text-center text-navy/60">{text}{cta && <div className="mt-3"><Button asChild className="rounded-full bg-coral text-white"><Link to="/book"><Plus size={16} className="mr-1" />Book a Lesson</Link></Button></div>}</div>);
}
function formatTime(t) { const [h, m] = t.split(":").map(Number); const ap = h >= 12 ? "PM" : "AM"; return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ap}`; }
